import { useState } from "react";
import { ChevronDown, Send } from "lucide-react";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", 
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", 
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", 
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", 
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", 
  "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi (NCT)", 
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const INQUIRY_TYPES = [
  "Product Inquiry", "Quotation Request", "Custom Wiring Harness Design", 
  "OEM / Bulk Order", "Technical Support", "Quality / Warranty Claim", 
  "Vendor Registration", "Careers", "Other",
];

const inputClasses =
  "w-full bg-[#e3f3fa] border border-gray-300 rounded-xl px-5 py-4 text-[#0A0A4A] placeholder-[#0A0A4A] focus:outline-none focus:ring-2 focus:ring-[#0A0A4A]/40";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    contact_number: "",
    region: "",
    inquiry_type: "",
    message: "",
  });
  const [status, setStatus] = useState(null); 
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validate = () => {
    const requiredFields = {
      Name: formData.name,
      Email: formData.email,
      Organization: formData.organization,
      "Contact Number": formData.contact_number,
      Region: formData.region,
      "Inquiry Type": formData.inquiry_type,
    };

    const missing = Object.entries(requiredFields)
      .filter(([, value]) => !value.trim())
      .map(([label]) => label);

    if (missing.length > 0) {
      return `Please fill in the following required field(s): ${missing.join(", ")}`;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(formData.email)) {
      return "Please enter a valid email address.";
    }

    return null;
  };

  const handleSubmit = async () => {
    console.log("--- START SUBMISSION PROCESS ---");
    console.log("[1/5] Current Form State Data:", formData);
    setStatus(null);

    const validationError = validate();
    if (validationError) {
      console.warn("[⚠️ Validation Failed]:", validationError);
      setStatus({ success: false, message: validationError });
      return;
    }
    console.log("[2/5] Client-side Validation Passed.");

    setSubmitting(true);

    try {
      const body = new FormData();
      Object.entries(formData).forEach(([key, value]) => body.append(key, value));
      
      console.log("[3/5] Dispatching Fetch Request to 'form.php'...");

      const response = await fetch("form.php", {
        method: "POST",
        body,
      });

      console.log("[4/5] Raw Network Response Status Received:", response.status, response.statusText);

      // Handle cases where the server throws a 500, 404, etc., or returns non-JSON pages
      if (!response.ok) {
        const textError = await response.text();
        console.error("[❌ Server Error Response Text]:", textError);
        throw new Error(`Server returned status code ${response.status}`);
      }

      const result = await response.json();
      console.log("[5/5] Parsed JSON Response from Backend:", result);

      if (result.debug) {
        console.log("🔍 PHP Server Internal Debug Diagnostics:", result.debug);
      }

      if (result.success) {
        console.log("🎉 Email successfully processed by backend!");
        setStatus({ success: true, message: result.message || "Thanks! Your message has been sent." });
        setFormData({
          name: "",
          email: "",
          organization: "",
          contact_number: "",
          region: "",
          inquiry_type: "",
          message: "",
        });
      } else {
        console.warn("⚠️ Backend responded with a failure status.");
        setStatus({ success: false, message: result.message || "Something went wrong. Please try again." });
      }
    } catch (err) {
      console.error("[❌ Operational Catch Triggered]:", err);
      setStatus({ success: false, message: "Network error or invalid server response. Check browser console." });
    } finally {
      setSubmitting(false);
      console.log("--- END SUBMISSION PROCESS ---");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-16  ">
      <h1 className="text-4xl md:text-5xl font-bold text-cogInk mb-4">
        Get answers to your questions
      </h1>
      <p className="text-lg text-[#0A0A4A] mb-10">
        From automotive to industrial applications, our wiring harness solutions keep
        your systems connected. Tell us what you need and our team will get back to you.
      </p>

      <div className="space-y-4">
        {/* Row 1: Name / Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Name*"
            value={formData.name}
            onChange={handleChange("name")}
            className={inputClasses}
          />
          <input
            type="email"
            placeholder="Email*"
            value={formData.email}
            onChange={handleChange("email")}
            className={inputClasses}
          />
        </div>

        {/* Row 2: Organization / Contact Number */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Organization*"
            value={formData.organization}
            onChange={handleChange("organization")}
            className={inputClasses}
          />
          <input
            type="tel"
            placeholder="Contact Number*"
            value={formData.contact_number}
            onChange={handleChange("contact_number")}
            className={inputClasses}
          />
        </div>

        {/* Row 3: Region (Indian States) / Inquiry Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <select
              value={formData.region}
              onChange={handleChange("region")}
              className={`${inputClasses} appearance-none pr-12`}
            >
              <option value="" disabled hidden>
                Region (State)*
              </option>
              {INDIAN_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A0A4A]" />
          </div>

          <div className="relative">
            <select
              value={formData.inquiry_type}
              onChange={handleChange("inquiry_type")}
              className={`${inputClasses} appearance-none pr-12`}
            >
              <option value="" disabled hidden>
                Inquiry Type*
              </option>
              {INQUIRY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A0A4A]" />
          </div>
        </div>

        {/* Row 4: Message */}
        <textarea
          placeholder="Message"
          rows={8}
          value={formData.message}
          onChange={handleChange("message")}
          className={`${inputClasses} resize-y`}
        />

        {/* Status message */}
        {status && (
          <div
            className={`text-sm font-medium rounded-lg px-4 py-3 ${
              status.success
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {status.message}
          </div>
        )}

        {/* Submit */}
        <div className="pt-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="group inline-flex items-center gap-2.5 bg-[#0A0A4A] text-white font-semibold pl-8 pr-6 py-4 rounded-xl hover:bg-[#0A0A4A]/90 active:scale-[0.98] transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
          >
            <span>{submitting ? "Sending..." : "Submit"}</span>
            <Send
              className={`w-4 h-4 transition-transform duration-200 ${
                submitting ? "animate-pulse" : "group-hover:translate-x-1 group-hover:-translate-y-0.5"
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}